import InfoTooltip from "../../components/common/InfoTooltip";
import { SALON_SPEAK_TOOLTIPS } from "../../constants/salonSpeakTooltips";
import { listSalonSpeakVoiceChoices } from "./speechUtils";

const DAILY_GOAL_OPTIONS = [1, 2, 3, 4, 5, 6, 8, 10];
const VOICE_CHOICES = listSalonSpeakVoiceChoices();

export default function SalonSpeakHome({
  firstName,
  streakCount,
  dailyGoal,
  completedLessonsToday,
  nextLesson,
  onOpenTab,
  onDailyGoalChange,
  selectedVoiceUri,
  onVoiceSelectionChange,
  onSaveVoiceSelection,
  isVoiceSelectionDirty,
  voiceSaving,
  voiceNotice,
}) {
  const safeGoal = Math.max(Number(dailyGoal || 3), 1);
  const safeCompleted = Math.max(Number(completedLessonsToday || 0), 0);
  const pct = Math.min(100, Math.round((safeCompleted / safeGoal) * 100));

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <h2 className="text-xl font-semibold text-slate-900">Xin chào, {firstName || "there"}!</h2>
        <p className="mt-1 text-sm text-slate-600">
          Practice salon English daily to improve confidence and upsell quality.
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm space-y-3">
          <div className="flex items-center justify-between gap-2">
            <div className="text-sm font-semibold text-slate-900 inline-flex items-center gap-2">
              Daily Goal
              <InfoTooltip text={SALON_SPEAK_TOOLTIPS.home.dailyGoal} label="Daily goal" />
            </div>
            <span className="rounded-full bg-emerald-100 px-2 py-1 text-xs font-semibold text-emerald-700">
              {safeCompleted}/{safeGoal} lessons
            </span>
          </div>
          <div className="h-2 rounded-full bg-slate-100">
            <div className="h-2 rounded-full bg-emerald-500" style={{ width: `${pct}%` }} />
          </div>
          <div className="flex items-center justify-between">
            <label className="text-xs uppercase tracking-wide text-slate-500 font-semibold">
              Update goal
            </label>
            <select
              value={safeGoal}
              onChange={(event) => onDailyGoalChange?.(Number(event.target.value))}
              className="h-9 rounded-xl border border-slate-300 bg-white px-2 text-sm"
            >
              {DAILY_GOAL_OPTIONS.map((value) => (
                <option key={value} value={value}>
                  {value} / day
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div className="text-sm font-semibold text-slate-900 inline-flex items-center gap-2">
              Streak
              <InfoTooltip text={SALON_SPEAK_TOOLTIPS.home.streak} label="Streak" />
            </div>
            <span className="rounded-full bg-amber-100 px-2 py-1 text-xs font-semibold text-amber-700">
              {Number(streakCount || 0)} day streak
            </span>
          </div>
          <p className="mt-3 text-sm text-slate-600">
            Keep at least one attempt per salon-local day to maintain your streak.
          </p>
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="text-sm font-semibold text-slate-900">Continue Learning</div>
            <p className="mt-1 text-sm text-slate-600">
              {nextLesson
                ? `Next lesson: ${nextLesson.title}`
                : "You finished current unlocked lessons. Great work."}
            </p>
          </div>
          <button
            type="button"
            onClick={() => onOpenTab?.("lessons")}
            className="h-10 rounded-full bg-slate-900 px-4 text-sm font-semibold text-white"
          >
            Open Lessons
          </button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div className="rounded-2xl border border-slate-200 bg-white p-4 text-left shadow-sm space-y-3">
          <div className="text-sm font-semibold text-slate-900">Voice Settings</div>
          <p className="text-sm text-slate-600">
            Choose an OpenAI voice for SalonSpeak playback. This setting applies across lessons, vocab, and quick phrases.
          </p>
          <div className="space-y-1">
            <label className="text-xs uppercase tracking-wide text-slate-500 font-semibold">
              Voice
            </label>
            <div className="flex items-center gap-2">
              <select
                value={selectedVoiceUri || ""}
                onChange={(event) => onVoiceSelectionChange?.(event.target.value)}
                className="h-10 flex-1 rounded-xl border border-slate-300 bg-white px-3 text-sm"
              >
                {VOICE_CHOICES.map((voice) => (
                  <option key={voice.voiceURI} value={voice.voiceURI}>
                    {voice.label}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => onSaveVoiceSelection?.()}
                disabled={!isVoiceSelectionDirty || voiceSaving}
                className="h-10 rounded-xl bg-slate-900 px-3 text-xs font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
              >
                {voiceSaving ? "Saving..." : "Save Voice"}
              </button>
            </div>
          </div>
          {voiceNotice ? <div className="text-xs text-emerald-700">{voiceNotice}</div> : null}
          <div className="text-xs text-slate-500">
            OpenAI voices are used for SalonSpeak audio. If AI voice is temporarily unavailable, playback falls back to your browser voice.
          </div>
        </div>

        <button
          type="button"
          onClick={() => onOpenTab?.("roleplay")}
          className="rounded-2xl border border-slate-200 bg-white p-4 text-left shadow-sm hover:bg-slate-50"
        >
          <div className="text-sm font-semibold text-slate-900">Quick Practice: Roleplay</div>
          <p className="mt-1 text-sm text-slate-600">
            Practice live response flow with client prompts.
          </p>
        </button>
        <button
          type="button"
          onClick={() => onOpenTab?.("phrases")}
          className="rounded-2xl border border-slate-200 bg-white p-4 text-left shadow-sm hover:bg-slate-50"
        >
          <div className="text-sm font-semibold text-slate-900">Quick Practice: Phrases</div>
          <p className="mt-1 text-sm text-slate-600">
            Tap-and-play useful daily salon lines.
          </p>
        </button>
      </div>
    </div>
  );
}
